import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, RefreshCw, Tag } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const iconOptions = [
  { value: '🏢', label: '🏢 Building' }, { value: '👷', label: '👷 Contractor' },
  { value: '⚙️', label: '⚙️ Service'  }, { value: '📦', label: '📦 Package'   },
  { value: '🚛', label: '🚛 Truck'    }, { value: '✉️', label: '✉️ Mail'      },
  { value: '🏭', label: '🏭 Factory'  }, { value: '🛃', label: '🛃 Customs'   },
  { value: '🛡️', label: '🛡️ Shield'  }, { value: '📋', label: '📋 List'      },
  { value: '🔧', label: '🔧 Tool'     }, { value: '💼', label: '💼 Briefcase' },
  { value: '🌐', label: '🌐 Globe'    }, { value: '📞', label: '📞 Phone'     },
  { value: '🎯', label: '🎯 Target'   },
];

const emptyForm = { suppliertypeid: null, typecode: '', typename: '', description: '', displayorder: 0, color: '', icon: '' };

const SupplierTypes = () => {
  const [types,         setTypes]         = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [formData,      setFormData]      = useState(emptyForm);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [searchTerm,    setSearchTerm]    = useState('');
  const [showForm,      setShowForm]      = useState(false);
  const [editingId,     setEditingId]     = useState(null);
  const [showWithCount, setShowWithCount] = useState(false);

  useEffect(() => { loadTypes(); }, [showWithCount]);

  useEffect(() => {
    if (!searchTerm.trim()) { setFilteredTypes(types); return; }
    const q = searchTerm.toLowerCase();
    setFilteredTypes(types.filter(t =>
      t.typecode.toLowerCase().includes(q) ||
      t.typename.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q)
    ));
  }, [searchTerm, types]);

  const loadTypes = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(showWithCount ? '/SupplierTypes.php?with_count' : '/SupplierTypes.php');
      if (res.data.success) { setTypes(res.data.data.types || []); setFilteredTypes(res.data.data.types || []); }
    } catch { setError('Failed to load supplier types'); }
    finally { setLoading(false); }
  };

  const generateTypeCode = async () => {
    try {
      const res = await apiClient.get('/SupplierTypes.php?generate_code');
      if (res.data.success) setFormData(p => ({ ...p, typecode: res.data.data.typecode }));
    } catch (e) { console.error(e); }
  };

  const handleChange       = (e) => { const { name, value } = e.target; setFormData(p => ({ ...p, [name]: value })); };
  const handleNumberChange = (e) => { const { name, value } = e.target; setFormData(p => ({ ...p, [name]: value === '' ? 0 : parseInt(value) })); };

  const validateForm = () => {
    if (!formData.typecode.trim()) { setError('Type code is required'); return false; }
    if (!formData.typename.trim()) { setError('Type name is required'); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true); setError('');
      const res = await apiClient[editingId ? 'put' : 'post']('/SupplierTypes.php', formData);
      if (res.data.success) { await loadTypes(); handleClear(); setShowForm(false); }
      else setError(res.data.message || 'Failed to save supplier type');
    } catch (err) { setError('Failed to save supplier type: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleEdit = (type) => {
    setFormData({ suppliertypeid: type.suppliertypeid, typecode: type.typecode || '', typename: type.typename || '', description: type.description || '', displayorder: parseInt(type.displayorder) || 0, color: type.color || '', icon: type.icon || '' });
    setEditingId(type.suppliertypeid); setShowForm(true); setError('');
  };

  const handleDelete = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this supplier type?')) return;
    try {
      setLoading(true);
      const res = await apiClient.delete('/SupplierTypes.php', { data: { suppliertypeid: typeId } });
      if (res.data.success) await loadTypes();
      else setError(res.data.message || 'Failed to delete supplier type');
    } catch (err) { setError('Failed to delete supplier type: ' + err.message); }
    finally { setLoading(false); }
  };

  const handleClear  = () => { setFormData(emptyForm); setEditingId(null); setError(''); };
  const handleNew    = async () => { handleClear(); await generateTypeCode(); setShowForm(true); };
  const handleCancel = () => { handleClear(); setShowForm(false); };

  const inputCls  = `w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] disabled:bg-gray-100 disabled:cursor-not-allowed`;
  const selectCls = `w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer`;
  const labelCls  = 'block mb-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wide';

  const colCount = showWithCount ? 7 : 6;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Tag size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Supplier Type Management</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage supplier type classifications</p>
          </div>
        </div>
        {!showForm && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer px-3.5 py-2 bg-white/15 border border-white/30 rounded-lg select-none hover:bg-white/25 transition-all duration-150">
              <input type="checkbox" checked={showWithCount} onChange={e => setShowWithCount(e.target.checked)}
                className="w-4 h-4 cursor-pointer accent-white" />
              <span className="text-sm font-semibold" style={{ color: '#fff' }}>Show Count</span>
            </label>
            <button onClick={handleNew}
              className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
              style={{ color: NAVY }}>
              <Plus size={17} /> New Type
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 p-5">

        {/* ── ERROR ── */}
        {error && (
          <div className="flex items-center gap-2.5 px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-semibold">
            <span>⚠️</span>
            <span className="flex-1">{error}</span>
            <button onClick={() => setError('')}
              className="text-red-500 hover:text-red-700 bg-transparent border-0 cursor-pointer text-lg leading-none">×</button>
          </div>
        )}

        {/* ── FORM ── */}
        {showForm && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-5 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="text-[17px] font-bold m-0" style={{ color: '#fff' }}>
                {editingId ? '✏️ Edit Supplier Type' : '➕ New Supplier Type'}
              </h3>
            </div>

            <div className="p-6">
              {/* Row 1 */}
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className={labelCls}>Type Code <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    <input type="text" name="typecode" value={formData.typecode}
                      onChange={handleChange} disabled={loading} placeholder="TYPE001"
                      className={inputCls + ' flex-1 uppercase'} />
                    {!editingId && (
                      <button type="button" onClick={generateTypeCode} disabled={loading}
                        className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer whitespace-nowrap hover:bg-gray-50 disabled:opacity-60">
                        <RefreshCw size={12} /> Gen
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  <label className={labelCls}>Type Name <span className="text-red-500">*</span></label>
                  <input type="text" name="typename" value={formData.typename}
                    onChange={handleChange} disabled={loading} placeholder="e.g., Vendor, Contractor, Service Provider"
                    className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Display Order</label>
                  <input type="number" name="displayorder" value={formData.displayorder}
                    onChange={handleNumberChange} disabled={loading} min="0" placeholder="0"
                    className={inputCls + ' text-right'} />
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="col-span-2">
                  <label className={labelCls}>Description</label>
                  <textarea name="description" value={formData.description}
                    onChange={handleChange} disabled={loading} rows={3}
                    placeholder="Brief description of this supplier type"
                    className={`${inputCls} resize-y font-[inherit] w-full`} />
                </div>
                <div>
                  <label className={labelCls}>Icon</label>
                  <select name="icon" value={formData.icon} onChange={handleChange} disabled={loading} className={selectCls}>
                    <option value="">-- Select Icon --</option>
                    {iconOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {formData.icon && (
                    <div className="mt-2 text-[2rem] text-center">{formData.icon}</div>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Color</label>
                  <input type="color" name="color" value={formData.color || '#0d1c6b'}
                    onChange={handleChange} disabled={loading}
                    className="w-full h-10 rounded-lg border-2 border-gray-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed p-1" />
                  {formData.color && (
                    <div className="text-[12px] text-gray-500 mt-1 font-mono text-center">{formData.color}</div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-5 border-t border-gray-200">
                <button onClick={handleSave} disabled={loading}
                  className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {loading ? '⏳ Saving...' : editingId ? '💾 Update' : '💾 Save'}
                </button>
                <button onClick={handleCancel} disabled={loading}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                  ❌ Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── LIST ── */}
        {!showForm && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200">
              <h3 className="text-[15px] font-bold text-gray-700 m-0">
                📋 Supplier Types List ({filteredTypes.length})
              </h3>
              <div className="flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
                focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] transition-all duration-200">
                <Search size={15} className="text-gray-400 flex-shrink-0" />
                <input type="text" placeholder="Search supplier types..."
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  className="border-0 outline-none py-2 text-sm text-gray-800 bg-transparent font-medium w-[200px] placeholder:text-gray-400 placeholder:font-normal" />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')}
                    className="p-0.5 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500">
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                    {['Icon', 'Code', 'Type Name', 'Description', 'Order', ...(showWithCount ? ['Suppliers'] : []), 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                        style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && filteredTypes.length === 0 ? (
                    <tr><td colSpan={colCount} className="text-center py-12 text-gray-400">Loading...</td></tr>
                  ) : filteredTypes.length === 0 ? (
                    <tr>
                      <td colSpan={colCount} className="text-center py-12 text-gray-400">
                        {searchTerm ? 'No supplier types found matching your search.' : 'No supplier types found. Click "New Type" to create one.'}
                      </td>
                    </tr>
                  ) : (
                    filteredTypes.map(type => (
                      <tr key={type.suppliertypeid}
                        className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                        <td className="px-4 py-3.5 text-center text-2xl">{type.icon || '📋'}</td>
                        <td className="px-4 py-3.5">
                          <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">
                            {type.typecode}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <span className="font-semibold" style={{ color: type.color || 'inherit' }}>
                            {type.typename}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-sm text-gray-500 max-w-[240px] truncate">{type.description || '-'}</td>
                        <td className="px-4 py-3.5 text-center font-semibold text-gray-600">{type.displayorder}</td>
                        {showWithCount && (
                          <td className="px-4 py-3.5 text-center">
                            <span className="inline-block px-2.5 py-1 bg-violet-100 text-violet-700 rounded-full text-[12px] font-bold">
                              {type.suppliercount || 0}
                            </span>
                          </td>
                        )}
                        <td className="px-4 py-3.5">
                          <div className="flex gap-1.5">
                            <button onClick={() => handleEdit(type)} title="Edit" disabled={loading}
                              className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5 disabled:opacity-60">
                              <Edit2 size={13} />
                            </button>
                            <button onClick={() => handleDelete(type.suppliertypeid)} title="Delete" disabled={loading}
                              className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5 disabled:opacity-60">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierTypes;